import { useRef, useCallback } from 'react';

interface UseTripleClickOptions {
  delay?: number; // Time window in ms for 3 clicks (default: 500ms)
  onTripleClick?: (event: MouseEvent | React.MouseEvent) => void;
}

export const useTripleClick = (options: UseTripleClickOptions = {}) => {
  const { delay = 500, onTripleClick } = options;
  const clickCount = useRef(0);
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const lastClickTime = useRef<number>(0);

  const handleClick = useCallback((event: MouseEvent) => {
    const now = Date.now();
    
    // Reset if too much time has passed since last click
    if (now - lastClickTime.current > delay) {
      clickCount.current = 0;
    }
    
    clickCount.current++;
    lastClickTime.current = now;
    
    // Clear existing timer
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
    }
    
    // Set new timer to check for triple click
    clickTimer.current = setTimeout(() => {
      if (clickCount.current === 3) {
        // Triple click detected
        if (onTripleClick) {
          onTripleClick(event);
        }
      }
      // Reset counter after timeout
      clickCount.current = 0;
    }, delay);
  }, [delay, onTripleClick]);

  const reset = useCallback(() => {
    clickCount.current = 0;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    lastClickTime.current = 0;
  }, []);

  return {
    handleClick,
    reset,
  };
};

